import React from 'react';
import { useTranslation } from 'react-i18next';

export interface DateTimePillProps {
  dateTime?: string;
  startTime?: string;
  className?: string;
  style?: React.CSSProperties;
}

function formatTimeHHmm(dateTime?: string): string {
  if (!dateTime) return '';
  try {
    return new Date(dateTime).toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/Amsterdam',
    });
  } catch {
    try {
      return new Date(dateTime).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return ''; }
  }
}

function formatAmsterdamDate(iso?: string, locale: string = 'en-GB'): string {
  if (!iso) return '';
  const l = locale === 'nl' ? 'nl-NL' : 'en-GB';
  try {
    const d = new Date(iso);
    const day = d.toLocaleString(l, { day: '2-digit', timeZone: 'Europe/Amsterdam' });
    const mon = d.toLocaleString(l, { month: 'short', timeZone: 'Europe/Amsterdam' });
    const year = d.toLocaleString(l, { year: 'numeric', timeZone: 'Europe/Amsterdam' });
    return `${day} ${mon} ${year}`;
  } catch {
    const d = new Date(iso);
    const day = d.toLocaleString(l, { day: '2-digit' });
    const mon = d.toLocaleString(l, { month: 'short' });
    const year = d.toLocaleString(l, { year: 'numeric' });
    return `${day} ${mon} ${year}`;
  }
}

const DateTimePill: React.FC<DateTimePillProps> = ({ dateTime, startTime, className, style }) => {
  const { i18n } = useTranslation();
  const weekday = React.useMemo(() => {
    if (!dateTime) return '';
    const l = i18n.language === 'nl' ? 'nl-NL' : 'en-GB';
    try {
      return new Date(dateTime).toLocaleDateString(l, { weekday: 'short', timeZone: 'Europe/Amsterdam' });
    } catch {
      return new Date(dateTime).toLocaleDateString(l, { weekday: 'short' });
    }
  }, [dateTime, i18n.language]);

  return (
    <div
      className={className}
      style={{
        flex: '0 0 120px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: 12,
        padding: '0.5rem 0.75rem',
        boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
        ...style,
      }}
      title={`${formatAmsterdamDate(dateTime, i18n.language)} • ${startTime || formatTimeHHmm(dateTime)}`}
    >
      <div style={{ fontSize: 12, opacity: 0.9, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}>
        {weekday}
        <span style={{ opacity: 0.8 }}>📅</span>
        <span>{formatAmsterdamDate(dateTime, i18n.language)}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700 }}>
        {startTime || formatTimeHHmm(dateTime)}
      </div>
    </div>
  );
};

export default DateTimePill;
